import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fileToImageAssets } from "../lib/media";
import AssignmentDialog from "./AssignmentDialog";
import { useCaptureContext } from "../context/CaptureContext";

const MAX_BATCH = 20;

/**
 * Image-upload pipeline (no UI of its own).
 *
 * Iter-123 — the floating bottom action bar was REMOVED entirely. The
 * single canonical Upload action now lives in the project header
 * (HeaderAddMenu) and dispatches an `onsetflow:capture` window event
 * which this component picks up. We keep the hidden file input here so
 * the picker, compression, batching and AssignmentDialog handoff all
 * stay in one place.
 *
 * Pilot users wanted exactly one upload path — no duplicates, no bottom
 * bar. CaptureFab is therefore now invisible by design; it's the
 * "engine" that the header (and any future single-upload surface) drives.
 */
export default function CaptureFab({ projectId }) {
    const capture = useCaptureContext();
    // useLocation is retained because future iterations may want to
    // gate the listener on certain surfaces (e.g. don't fire on the
    // login screen); currently a no-op.
    const location = useLocation();
    void location;
    const photosRef = useRef(null);

    const isCastSurface = false;

    const [dialogOpen, setDialogOpen] = useState(false);
    const [pendingBatch, setPendingBatch] = useState([]);
    const [preparing, setPreparing] = useState(false);
    const [pendingContext, setPendingContext] = useState(null);

    const openPhotos = () => photosRef.current?.click();

    useEffect(() => {
        const onAction = (ev) => {
            const action = ev?.detail?.action;
            const ctx = ev?.detail?.context || null;
            setPendingContext(ctx);
            // Iter-122 — camera disabled globally. Any legacy
            // dispatch with action="camera" gracefully falls back
            // to the photos picker with a one-line explainer so
            // pilot users always know what just happened.
            if (action === "camera") {
                toast.message(
                    "Camera capture is temporarily disabled. Please upload from your device.",
                );
            }
            openPhotos();
        };
        window.addEventListener("onsetflow:capture", onAction);
        return () => window.removeEventListener("onsetflow:capture", onAction);
    }, []);

    const handleFiles = async (e) => {
        const fileList = Array.from(e.target.files || []);
        e.target.value = "";
        if (!fileList.length) return;
        const images = fileList.filter((f) => f.type.startsWith("image/"));
        if (!images.length) {
            toast.error("Please pick image files only.");
            return;
        }
        let trimmed = images;
        if (images.length > MAX_BATCH) {
            trimmed = images.slice(0, MAX_BATCH);
            toast.warning(
                `Picked ${images.length} — using the first ${MAX_BATCH} (max per batch).`,
            );
        }
        setPreparing(true);
        try {
            const out = [];
            for (const file of trimmed) {
                // eslint-disable-next-line no-await-in-loop
                const assets = await fileToImageAssets(file);
                out.push({ ...assets, fileName: file.name, file });
            }
            setPendingBatch(out);
            // iter-73 PART 1 — On Set is the unconditional default
            // for upload flows. Explicit dispatched contexts (e.g.
            // "Capture from this fitting card") still win.
            if (!pendingContext) {
                setPendingContext({ mode: "onset" });
            }
            setDialogOpen(true);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            toast.error("Couldn't read one of the images. Try again.");
        } finally {
            setPreparing(false);
        }
    };

    const reset = () => {
        setPendingBatch([]);
        setPendingContext(null);
    };

    const onSaved = () => {
        capture?.refreshCount();
        reset();
        window.dispatchEvent(new CustomEvent("onsetflow:state-entry-saved"));
    };
    const onLater = () => {
        capture?.refreshCount();
        reset();
        window.dispatchEvent(new CustomEvent("onsetflow:state-entry-saved"));
    };

    // Iter-123 — the bottom action bar (and its secondary icon
    // sibling) was deleted entirely. CaptureFab now renders a
    // hidden file input + the AssignmentDialog only. The single
    // canonical Upload entry point lives in the project header
    // (HeaderAddMenu) and dispatches `onsetflow:capture` events
    // we listen for above. `isCastSurface` is no longer
    // referenced — left in place for future page-context
    // decisions but currently a no-op.
    void isCastSurface;
    void preparing;

    return (
        <>
            {/* Iter-124 — narrow `accept` to the specific still-image
                MIME types rather than the wildcard `image/*`. On many
                mobile browsers the wildcard surfaces a "Take photo"
                option in the OS picker sheet; the explicit list
                suppresses it. Cannot be fully guaranteed across every
                device (iOS Safari, some Android launchers still
                include camera) but it's the most effective web-only
                hint available. */}
            <input
                ref={photosRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/heic,image/heif,image/gif"
                multiple
                className="hidden"
                data-testid="fab-photos-input"
                data-upload-input="true"
                onChange={handleFiles}
            />

            {pendingBatch.length > 0 ? (
                <AssignmentDialog
                    projectId={projectId}
                    open={dialogOpen}
                    mode="create"
                    images={pendingBatch}
                    initialContext={pendingContext}
                    onClose={() => {
                        setDialogOpen(false);
                        reset();
                    }}
                    onSaved={onSaved}
                    onAssignLater={onLater}
                />
            ) : null}
        </>
    );
}
